import React, { useRef, useState } from "react";
import { AppShell } from "../../components/Sidebar";
import { API } from "../../lib/api";
import { Download, UploadCloud, ShieldAlert, CheckCircle2, Database } from "lucide-react";
import { toast } from "sonner";

export default function BackupRestore() {
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState("merge");
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const doExport = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("gd_token");
      const res = await fetch(`${API}/admin/backup/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `gyandhara-backup-${ts}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDownloading(false);
    }
  };

  const doImport = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a backup file");
    if (mode === "replace" && !window.confirm(
      "REPLACE mode will WIPE all matching collections before importing. Continue?"
    )) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("gd_token");
      const res = await fetch(`${API}/admin/backup/import?mode=${mode}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Import failed");
      setResult(data);
      toast.success("Backup restored");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <AppShell role="admin">
      <div className="flex items-center gap-3">
        <Database className="text-indigo-600" size={26} />
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Backup &amp; Restore</h1>
      </div>
      <p className="mt-1 text-slate-500 max-w-2xl">
        Export the full application database as a JSON file, or restore from a previously
        downloaded backup. Use this to sync data between Preview and Production, or to keep
        an off-container backup of your students, schools, modules and VR activity.
      </p>

      <div className="mt-7 grid gap-6 lg:grid-cols-2">
        {/* EXPORT */}
        <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-card" data-testid="backup-export-card">
          <div className="flex items-center gap-2">
            <Download size={18} className="text-emerald-600" />
            <h2 className="text-lg font-bold text-slate-900">Export full database</h2>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Downloads a JSON file with every school, user, module, quiz and VR record.
            Safe to run any time.
          </p>
          <button
            onClick={doExport}
            disabled={downloading}
            data-testid="backup-export-btn"
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
          >
            <Download size={16} />
            {downloading ? "Preparing…" : "Download backup"}
          </button>
        </section>

        {/* IMPORT */}
        <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-card" data-testid="backup-import-card">
          <div className="flex items-center gap-2">
            <UploadCloud size={18} className="text-indigo-600" />
            <h2 className="text-lg font-bold text-slate-900">Restore from backup</h2>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Upload a <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">gyandhara-backup-*.json</code> file
            downloaded from another environment.
          </p>

          <form onSubmit={doImport} className="mt-5 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Mode</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setMode("merge")}
                  data-testid="backup-mode-merge"
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${mode === "merge" ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
                  Merge (safe, upsert)
                </button>
                <button type="button" onClick={() => setMode("replace")}
                  data-testid="backup-mode-replace"
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${mode === "replace" ? "border-rose-500 bg-rose-50 text-rose-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
                  Replace (wipes existing)
                </button>
              </div>
              {mode === "replace" && (
                <p className="mt-2 flex items-start gap-1.5 text-xs text-rose-600">
                  <ShieldAlert size={14} className="mt-0.5 shrink-0" />
                  This drops every listed collection before inserting. Use only when you want an exact mirror.
                </p>
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">Backup file</label>
              <input
                ref={fileRef}
                type="file"
                accept="application/json,.json"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="backup-import-file"
                className="block w-full rounded-lg border border-slate-200 bg-white p-2 text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200"
              />
            </div>

            <button
              type="submit"
              disabled={uploading || !file}
              data-testid="backup-import-btn"
              className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-60"
            >
              <UploadCloud size={16} />
              {uploading ? "Restoring…" : "Restore backup"}
            </button>
          </form>
        </section>
      </div>

      {result && (
        <section className="mt-7 rounded-2xl border border-slate-100 bg-white p-6 shadow-card" data-testid="backup-import-result">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={18} className="text-emerald-600" />
            <h3 className="text-lg font-bold text-slate-900">Restore complete</h3>
          </div>
          {result.exported_at && (
            <p className="mt-1 text-sm text-slate-500">
              Source backup taken at <span className="font-mono">{result.exported_at}</span>
            </p>
          )}
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[520px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-4">Collection</th>
                  <th className="py-2 pr-4">Inserted</th>
                  <th className="py-2 pr-4">Updated</th>
                  <th className="py-2 pr-4">Total in file</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(result.summary || {}).map(([name, s]) => (
                  <tr key={name} className="border-b border-slate-50">
                    <td className="py-2 pr-4 font-medium text-slate-900">{name}</td>
                    <td className="py-2 pr-4 text-emerald-700">{s.inserted}</td>
                    <td className="py-2 pr-4 text-indigo-700">{s.updated}</td>
                    <td className="py-2 pr-4 text-slate-600">{s.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </AppShell>
  );
}
